import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { ptBR } from 'date-fns/locale/pt-BR';
import Select from "react-select";
import { toast } from "react-toastify";
import { apiService } from '../../../services/api';
import {
  ContaPagar,
  CreateContaPagarData,
  UpdateContaPagarData,
  ContaPagarFilters,
  ProcessarPagamentoData,
  SituacaoPagamento,
  FormaPagamento,
  CategoriaFinanceira
} from '../../../types/financeiro';
import ScrollableTable from '../contracts/components/ScrollableTable';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MONTHS_SHORT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const customPtBR = {
  ...ptBR,
  localize: {
    ...ptBR.localize,
    month: (n: number) => MONTHS[n],
    monthShort: (n: number) => MONTHS_SHORT[n]
  }
};

registerLocale('pt-BR', customPtBR as any);

// Opções para os selects
// Temporário: Opções fixas para formas de pagamento (depois será dinâmico via API)
const formaPagamentoOptions = [
  { value: 1, label: 'PIX' },
  { value: 2, label: 'Cartão de Crédito' },
  { value: 3, label: 'Cartão de Débito' },
  { value: 4, label: 'Boleto' },
  { value: 5, label: 'Transferência Bancária' },
  { value: 6, label: 'Dinheiro' }
];

const situacaoOptions = [
  { value: 'EM_ABERTO', label: 'Em Aberto' },
  { value: 'PAGO', label: 'Pago' },
  { value: 'PARCIALMENTE_PAGO', label: 'Parcialmente Pago' },
  { value: 'VENCIDO', label: 'Vencido' },
  { value: 'CANCELADO', label: 'Cancelado' }
];

// Interface para os cards de resumo
interface CardResumo {
  vencidos: number;
  vencidos_valor: number;
  vencem_hoje: number;
  vencem_hoje_valor: number;
  a_vencer: number;
  a_vencer_valor: number;
  pagos: number;
  pagos_valor: number;
  total_periodo: number;
  total_periodo_valor: number;
}

// Interface para arquivos anexados
interface UploadedFile {
  file: File;
  size: string;
  name: string;
}

const ContasAPagar = () => {
  // Estados principais
  const [contas, setContas] = useState<ContaPagar[]>([]);
  const [filteredContas, setFilteredContas] = useState<ContaPagar[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);


  // Estados para resumo (cards)
  const [resumo, setResumo] = useState<CardResumo>({
    vencidos: 0,
    vencidos_valor: 0,
    vencem_hoje: 0,
    vencem_hoje_valor: 0,
    a_vencer: 0,
    a_vencer_valor: 0,
    pagos: 0,
    pagos_valor: 0,
    total_periodo: 0,
    total_periodo_valor: 0
  });

  // Estados para formulários
  const [contaSelecionada, setContaSelecionada] = useState<ContaPagar | null>(null);
  const [formData, setFormData] = useState<CreateContaPagarData>({
    id_fornecedor: 0,
    id_categoria: 0,
    descricao: '',
    valor_original: 0,
    data_emissao: '',
    data_vencimento: ''
  });
  const [updateFormData, setUpdateFormData] = useState<UpdateContaPagarData>({});
  const [pagamentoData, setPagamentoData] = useState<ProcessarPagamentoData>({
    valor: 0,
    id_forma_pagamento: 0, // Será definido dinamicamente
    data_pagamento: ''
  });

  // Estados para datas
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedDateVencimento, setSelectedDateVencimento] = useState<Date | null>(new Date());
  const [selectedDateEdit, setSelectedDateEdit] = useState<Date | null>(null);
  const [selectedDateVencimentoEdit, setSelectedDateVencimentoEdit] = useState<Date | null>(null);
  const [selectedDatePagamento, setSelectedDatePagamento] = useState<Date | null>(new Date());

  // Estados para filtro de período
  const [dataInicio, setDataInicio] = useState<Date | null>(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dataFim, setDataFim] = useState<Date | null>(new Date());

  // Estados para opções dos selects
  const [fornecedores, setFornecedores] = useState<{ value: number, label: string }[]>([]);
  const [categorias, setCategorias] = useState<{ value: number, label: string }[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<{ value: number, label: string }[]>([]);
  const [formaPagamentoPadrao, setFormaPagamentoPadrao] = useState<number | null>(null);

  // Estados para controle
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [pagamentoId, setPagamentoId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Estados para anexo de documento no cadastro
  const [anexoDataCreate, setAnexoDataCreate] = useState({
    titulo: '',
    descricao: ''
  });
  const [uploadedFilesCreate, setUploadedFilesCreate] = useState<UploadedFile[]>([]);
  const fileInputCreateRef = useRef<HTMLInputElement>(null);

  // Estados para anexo de documento na edição/posterior
  const [anexoData, setAnexoData] = useState({
    titulo: '',
    descricao: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [anexoContaId, setAnexoContaId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Função para formatar moeda
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função auxiliar para formatar o tamanho do arquivo
  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Função para obter cor da situação
  const getSituacaoColor = (situacao: SituacaoPagamento): string => {
    switch (situacao) {
      case 'PAGO': return 'bg-success';
      case 'EM_ABERTO': return 'bg-secondary';
      case 'PARCIALMENTE_PAGO': return 'bg-warning';
      case 'VENCIDO': return 'bg-danger';
      case 'CANCELADO': return 'bg-dark';
      default: return 'bg-secondary';
    }
  };

  // Função para calcular resumo dos cards
  const calcularResumo = (contasData: ContaPagar[]): CardResumo => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    let vencidos = 0, vencidos_valor = 0;
    let vencem_hoje = 0, vencem_hoje_valor = 0;
    let a_vencer = 0, a_vencer_valor = 0;
    let pagos = 0, pagos_valor = 0;

    contasData.forEach(conta => {
      const dataVencimento = new Date(conta.data_vencimento + 'T00:00:00');
      dataVencimento.setHours(0, 0, 0, 0);

      if (conta.situacao === 'PAGO') {
        pagos++;
        pagos_valor += conta.valor_pago;
      } else if (conta.situacao === 'VENCIDO' || (dataVencimento < hoje && conta.situacao === 'EM_ABERTO')) {
        vencidos++;
        vencidos_valor += conta.valor_saldo;
      } else if (dataVencimento.getTime() === hoje.getTime()) {
        vencem_hoje++;
        vencem_hoje_valor += conta.valor_saldo;
      } else if (dataVencimento > hoje) {
        a_vencer++;
        a_vencer_valor += conta.valor_saldo;
      }
    });

    return {
      vencidos,
      vencidos_valor,
      vencem_hoje,
      vencem_hoje_valor,
      a_vencer,
      a_vencer_valor,
      pagos,
      pagos_valor,
      total_periodo: contasData.length,
      total_periodo_valor: contasData.reduce((acc, conta) => acc + conta.valor_original, 0)
    };
  };

  // Função para forçar fechamento de modal
  const forceCloseModal = (modalId: string): void => {
    document.body.classList.remove('modal-open');
    document.body.removeAttribute('data-bs-overflow');
    document.body.removeAttribute('data-bs-padding-right');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');

    const backdrops = document.getElementsByClassName('modal-backdrop');
    while (backdrops.length > 0) {
      backdrops[0].parentNode?.removeChild(backdrops[0]);
    }

    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.removeAttribute('role');
      modalElement.classList.remove('show');
    }
  };

  // Manipuladores de arquivo para anexo no cadastro
  const handleFileUploadCreate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files).map(file => ({
        file: file,
        size: formatFileSize(file.size),
        name: file.name
      }));
      setUploadedFilesCreate(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFileCreate = (index: number) => {
    setUploadedFilesCreate(prev => prev.filter((_, i) => i !== index));
  };

  // Manipuladores de arquivo para anexo posterior
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files).map(file => ({
        file: file,
        size: formatFileSize(file.size),
        name: file.name
      }));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Limpar dados do modal de anexo
  const clearAnexoData = () => {
    setAnexoData({
      titulo: '',
      descricao: ''
    });
    setUploadedFiles([]);
    setAnexoContaId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Limpar dados do anexo no cadastro
  const clearAnexoDataCreate = () => {
    setAnexoDataCreate({
      titulo: '',
      descricao: ''
    });
    setUploadedFilesCreate([]);
    if (fileInputCreateRef.current) {
      fileInputCreateRef.current.value = '';
    }
  };

  // Função para abrir modal de anexo
  const handleAnexarDocumento = (id: number) => {
    setAnexoContaId(id);
  };

  // Enviar anexo posterior
  const handleSubmitAnexo = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!anexoData.titulo.trim()) {
        toast.error("O título é obrigatório");
        return;
      }

      if (uploadedFiles.length === 0) {
        toast.error("Selecione pelo menos um arquivo para upload");
        return;
      }

      if (!anexoContaId) {
        toast.error("Erro: ID da conta não identificado");
        return;
      }

      setIsSubmitting(true);

      // Aqui você implementaria a chamada para a API de anexos
      await apiService.createAnexoFinanceiro({
        tipo_origem: 'CONTA_PAGAR',
        id_origem: anexoContaId,
        titulo: anexoData.titulo,
        descricao: anexoData.descricao,
        arquivo: uploadedFiles[0].file
      });

      toast.success("Documento anexado com sucesso!");

      forceCloseModal('anexar_documento');
      clearAnexoData();

    } catch (error) {
      console.error("Erro ao anexar documento:", error);
      toast.error("Erro ao anexar documento");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Buscar dados iniciais
  const fetchDadosIniciais = async () => {
    try {
      setIsLoading(true);

      // Buscar fornecedores
      try {
        const fornecedoresResponse = await apiService.getFornecedores();
        if (fornecedoresResponse.success && fornecedoresResponse.data) {
          const fornecedoresOptions = fornecedoresResponse.data.map(fornecedor => ({
            value: fornecedor.id,
            label: fornecedor.nome
          }));
          setFornecedores(fornecedoresOptions);
        }
      } catch (err) {
        console.error('Erro ao buscar fornecedores:', err);
      }

      // Buscar categorias financeiras
      try {
        const categoriasResponse = await apiService.getCategoriaFinanceira();
        if (categoriasResponse.success && categoriasResponse.data) {
          let categoriasArray: CategoriaFinanceira[] = [];

          if (Array.isArray(categoriasResponse.data)) {
            categoriasArray = categoriasResponse.data as CategoriaFinanceira[];
          } else {
            const responseData = categoriasResponse.data as any;
            categoriasArray = responseData.data || [];
          }

          const categoriasDespesa = categoriasArray
            .filter(categoria => categoria.tipo === 'DESPESA')
            .map(categoria => ({
              value: categoria.id,
              label: categoria.nome
            }));
          setCategorias(categoriasDespesa);
        }
      } catch (err) {
        console.error('Erro ao buscar categorias:', err);
      }

      // Buscar formas de pagamento
      try {
        const formasResponse = await apiService.getFormasPagamento();
        if (formasResponse.success && formasResponse.data) {
          let formasArray: any[] = [];

          if (Array.isArray(formasResponse.data)) {
            formasArray = formasResponse.data;
          } else {
            const responseData = formasResponse.data as any;
            formasArray = responseData.data || [];
          }

          const formasOptions = formasArray
            .filter(forma => forma.ativo)
            .map(forma => ({
              value: forma.id,
              label: forma.nome
            }));
          setFormasPagamento(formasOptions);

          // Buscar forma de pagamento padrão
          const formaPadrao = formasArray.find(forma => forma.padrao && forma.ativo);
          if (formaPadrao) {
            setFormaPagamentoPadrao(formaPadrao.id);
          } else if (formasOptions.length > 0) {
            // Se não houver padrão definido, use a primeira forma ativa
            setFormaPagamentoPadrao(formasOptions[0].value);
          }
        }
      } catch (err) {
        console.error('Erro ao buscar formas de pagamento:', err);
        // Fallback para as opções fixas caso a API não esteja funcionando
        setFormasPagamento(formaPagamentoOptions);
        setFormaPagamentoPadrao(1); // PIX como fallback
      }

      await fetchContasPagar();

    } catch (err) {
      console.error('Erro ao buscar dados iniciais:', err);
      setError('Erro ao carregar dados iniciais');
    } finally {
      setIsLoading(false);
    }
  };

  // Buscar contas a pagar
  const fetchContasPagar = async () => {
    try {
      const filtros: ContaPagarFilters = {
        vencimento_de: dataInicio ? dataInicio.toISOString().split('T')[0] : undefined,
        vencimento_ate: dataFim ? dataFim.toISOString().split('T')[0] : undefined,
        page: 1,
        per_page: 100
      };

      const response = await apiService.getContasPagar(filtros);

      if (response.success && response.data) {
        const contasComKey = response.data.map((conta: ContaPagar) => ({
          ...conta,
          key: conta.id.toString(),
          valor_original: parseFloat(conta.valor_original.toString()) || 0,
          valor_pago: parseFloat(conta.valor_pago.toString()) || 0,
          valor_saldo: parseFloat(conta.valor_saldo.toString()) || 0
        }));

        setContas(contasComKey);
        setFilteredContas(contasComKey);

        const novoResumo = calcularResumo(contasComKey);
        setResumo(novoResumo);
      } else {
        setError('Não foi possível obter os dados das contas a pagar');
      }
    } catch (err) {
      console.error('Erro ao buscar contas a pagar:', err);
      setError('Erro ao carregar dados das contas a pagar');
    }
  };

  // Função para limpar pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para editar conta
  const handleEditConta = async (id: number) => {
    try {
      setEditError(null); // ADICIONAR ESTA LINHA
      const conta = contas.find(c => c.id === id);
      if (conta) {
        setContaSelecionada(conta);
        setUpdateFormData({
          descricao: conta.descricao,
          valor_original: conta.valor_original,
          id_categoria: conta.id_categoria,
          id_fornecedor: conta.id_fornecedor,
          numero_documento: conta.numero_documento,
          observacoes: conta.observacoes
        });
        setSelectedDateEdit(new Date(conta.data_emissao + 'T00:00:00'));
        setSelectedDateVencimentoEdit(new Date(conta.data_vencimento + 'T00:00:00'));
      }
    } catch (err) {
      console.error('Erro ao preparar edição:', err);
      setEditError('Erro ao carregar dados da conta');
    }
  };

  // Função para submeter edição
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contaSelecionada) return;

    try {
      setIsLoading(true);
      setEditError(null); // LIMPAR ERRO ANTERIOR

      let dataEmissaoFormatada = undefined;
      if (selectedDateEdit) {
        const year = selectedDateEdit.getFullYear();
        const month = String(selectedDateEdit.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDateEdit.getDate()).padStart(2, '0');
        dataEmissaoFormatada = `${year}-${month}-${day}`;
      }

      let dataVencimentoFormatada = undefined;
      if (selectedDateVencimentoEdit) {
        const year = selectedDateVencimentoEdit.getFullYear();
        const month = String(selectedDateVencimentoEdit.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDateVencimentoEdit.getDate()).padStart(2, '0');
        dataVencimentoFormatada = `${year}-${month}-${day}`;
      }

      const dados = {
        ...updateFormData,
        data_emissao: dataEmissaoFormatada,
        data_vencimento: dataVencimentoFormatada,
        id_conta_bancaria_padrao: 1 // Conta bancária padrão
      };

      const response = await apiService.updateContaPagar(contaSelecionada.id, dados);

      if (response.success) {
        const closeBtn = document.querySelector('#offcanvas_edit .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        await fetchContasPagar();
        toast.success('Conta atualizada com sucesso!');
      } else {
        setEditError('Erro ao atualizar conta: ' + response.message); // USAR setEditError
      }
    } catch (err: any) {
      console.error('Erro ao atualizar conta:', err);
      setEditError('Erro ao atualizar conta: ' + (err.message || 'Erro desconhecido')); // USAR setEditError
    } finally {
      setIsLoading(false);
    }
  };

  // Função para criar conta
  const handleCreateConta = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      let dataEmissaoFormatada = '';
      if (selectedDate) {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        dataEmissaoFormatada = `${year}-${month}-${day}`;
      }

      let dataVencimentoFormatada = '';
      if (selectedDateVencimento) {
        const year = selectedDateVencimento.getFullYear();
        const month = String(selectedDateVencimento.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDateVencimento.getDate()).padStart(2, '0');
        dataVencimentoFormatada = `${year}-${month}-${day}`;
      }

      const dados: CreateContaPagarData = {
        ...formData,
        data_emissao: dataEmissaoFormatada,
        data_vencimento: dataVencimentoFormatada,
        id_conta_bancaria_padrao: 1 // Conta bancária padrão
      };

      const response = await apiService.createContaPagar(dados);

      if (response.success) {
        // Se houver arquivos para anexar, anexá-los após criar a conta
        if (uploadedFilesCreate.length > 0 && anexoDataCreate.titulo.trim()) {
          try {
            await apiService.createAnexoFinanceiro({
              tipo_origem: 'CONTA_PAGAR',
              id_origem: response.data.id, // ID da conta recém-criada
              titulo: anexoDataCreate.titulo,
              descricao: anexoDataCreate.descricao,
              arquivo: uploadedFilesCreate[0].file
            });
            toast.success('Conta criada e documento anexado com sucesso!');
          } catch (anexoError) {
            console.error('Erro ao anexar documento:', anexoError);
            toast.warning('Conta criada, mas houve erro ao anexar o documento');
          }
        } else {
          toast.success('Conta criada com sucesso!');
        }

        const closeBtn = document.querySelector('#offcanvas_add .btn-close') as HTMLElement;
        if (closeBtn) closeBtn.click();

        // Limpar formulário
        setFormData({
          id_fornecedor: 0,
          id_categoria: 0,
          descricao: '',
          valor_original: 0,
          data_emissao: '',
          data_vencimento: ''
        });
        setSelectedDate(new Date());
        setSelectedDateVencimento(new Date());
        clearAnexoDataCreate();

        await fetchContasPagar();
      } else {
        setError('Erro ao criar conta: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao criar conta:', err);
      setError('Erro ao criar conta: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para processar pagamento
  const handleProcessarPagamento = (id: number) => {
    const conta = contas.find(c => c.id === id);
    if (conta) {
      setPagamentoId(id);
      setPagamentoData({
        valor: conta.valor_saldo,
        id_forma_pagamento: formaPagamentoPadrao || 1, // Usar forma padrão dinâmica
        data_pagamento: ''
      });
    }
  };

  // Confirmar pagamento
  const confirmProcessarPagamento = async () => {
    if (!pagamentoId) return;

    try {
      setIsProcessing(true);

      let dataPagamentoFormatada = '';
      if (selectedDatePagamento) {
        const year = selectedDatePagamento.getFullYear();
        const month = String(selectedDatePagamento.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDatePagamento.getDate()).padStart(2, '0');
        dataPagamentoFormatada = `${year}-${month}-${day}`;
      }

      const dados = {
        ...pagamentoData,
        data_pagamento: dataPagamentoFormatada,
        id_conta_bancaria: 1 // Conta bancária padrão
      };

      console.log("Enviando dados de pagamento:", { id: pagamentoId, dados });

      const response = await apiService.processarPagamento(pagamentoId, dados);

      if (response.success) {
        forceCloseModal('processar_pagamento');

        setPagamentoData({
          valor: 0,
          id_forma_pagamento: formaPagamentoPadrao || 1, // Usar forma padrão dinâmica
          data_pagamento: ''
        });
        setPagamentoId(null);

        await fetchContasPagar();
        toast.success("Pagamento processado com sucesso!");
      } else {
        setError('Erro ao processar pagamento: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao processar pagamento:', err);
      setError('Erro ao processar pagamento: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Função para deletar conta
  const handleDeleteConta = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };


  // Confirmar exclusão
  const confirmDeleteConta = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);
      const response = await apiService.deleteContaPagar(deleteId);

      if (response.success) {
        forceCloseModal('delete_conta');
        await fetchContasPagar();
        toast.success("Conta excluída com sucesso!");
      } else {
        setDeleteError('Erro ao excluir conta: ' + response.message);
      }
    } catch (err: any) {
      console.error('Erro ao excluir conta:', err);
      setDeleteError('Erro ao excluir conta: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  // Função para manipular mudanças no formulário
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'valor_original' || name === 'id_fornecedor' || name === 'id_categoria'
        ? parseFloat(value) || 0
        : value
    });
  };

  // Função para manipular mudanças no formulário de edição
  const handleUpdateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUpdateFormData({
      ...updateFormData,
      [name]: name === 'valor_original' || name === 'id_fornecedor' || name === 'id_categoria'
        ? parseFloat(value) || 0
        : value
    });
  };

  // Effects
  useEffect(() => {
    fetchDadosIniciais();
  }, []);

  useEffect(() => {
    if (dataInicio && dataFim) {
      fetchContasPagar();
    }
  }, [dataInicio, dataFim]);

  // Filtro de pesquisa com debounce
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredContas(contas);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = contas.filter(conta => {
        const searchableFields = [
          conta.descricao,
          conta.fornecedor_nome,
          conta.categoria_nome,
          conta.numero_documento,
          formatCurrency(conta.valor_original)
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredContas(filtered);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchText, contas]);

  // Definir as colunas da tabela
  const columns = [
    {
      title: "Data Vencimento",
      dataIndex: "data_vencimento",
      key: "data_vencimento",
      sorter: true,
      render: (text: string) => {
        const date = new Date(text + 'T00:00:00');
        return (
          <div className="d-flex align-items-center">
            <span>{date.toLocaleDateString('pt-BR')}</span>
          </div>
        );
      }
    },
    {
      title: "Data Pagamento",
      dataIndex: "data_pagamento",
      key: "data_pagamento",
      render: (text: string) => {
        if (!text) return <span className="text-muted">-</span>;
        const date = new Date(text + 'T00:00:00');
        return (
          <div className="d-flex align-items-center">
            <span>{date.toLocaleDateString('pt-BR')}</span>
          </div>
        );
      }
    },
    {
      title: "Descrição",
      dataIndex: "descricao",
      key: "descricao",
      render: (text: string, record: ContaPagar) => (
        <div className="d-flex flex-column">
          <span className="fw-semibold">{text}</span>
          {record.categoria_nome && (
            <small className="text-muted">{record.categoria_nome}</small>
          )}
          {record.fornecedor_nome && (
            <small className="text-muted">{record.fornecedor_nome}</small>
          )}
        </div>
      ),
    },
    {
      title: "Total",
      dataIndex: "valor_original",
      key: "valor_original",
      sorter: true,
      render: (value: number) => (
        <div className="text-end">
          <span className="fw-bold text-danger">
            {formatCurrency(value)}
          </span>
        </div>
      ),
    },
    {
      title: "Total a Pagar",
      dataIndex: "valor_saldo",
      key: "valor_saldo",
      sorter: true,
      render: (value: number, record: ContaPagar) => (
        <div className="text-end">
          <div className="fw-bold text-warning">
            {formatCurrency(value)}
          </div>
          {record.valor_pago > 0 && (
            <small className="text-success">
              Pago: {formatCurrency(record.valor_pago)}
            </small>
          )}
        </div>
      ),
    },
    {
      title: "Situação",
      dataIndex: "situacao",
      key: "situacao",
      render: (text: SituacaoPagamento) => (
        <span className={`badge badge-pill ${getSituacaoColor(text)}`}>
          {situacaoOptions.find(opt => opt.value === text)?.label || text}
        </span>
      ),
      sorter: true,
    },
    {
      title: "Ações",
      dataIndex: "acoes",
      key: "acoes",
      render: (text: string, record: ContaPagar) => (
        <div className="d-flex align-items-center gap-2">
          <Link
            className="btn btn-sm btn-outline-primary"
            data-bs-toggle="offcanvas"
            data-bs-target="#offcanvas_edit"
            to="#"
            onClick={() => handleEditConta(record.id)}
            title="Editar"
          >
            <i className="ti ti-edit"></i>
          </Link>

          {(record.situacao === 'EM_ABERTO' || record.situacao === 'PARCIALMENTE_PAGO' || record.situacao === 'VENCIDO') && (
            <Link
              className="btn btn-sm btn-outline-success"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#processar_pagamento"
              onClick={() => handleProcessarPagamento(record.id)}
              title="Pagar"
            >
              <i className="ti-credit-card"></i>
            </Link>
          )}

          <Link
            className="btn btn-sm btn-outline-info"
            to="#"
            data-bs-toggle="modal"
            data-bs-target="#anexar_documento"
            onClick={() => handleAnexarDocumento(record.id)}
            title="Anexar Documento"
          >
            <i className="ti ti-paperclip"></i>
          </Link>

          <Link
            className="btn btn-sm btn-outline-danger"
            to="#"
            data-bs-toggle="modal"
            data-bs-target="#delete_conta"
            onClick={() => handleDeleteConta(record.id)}
            title="Excluir"
          >
            <i className="ti ti-trash"></i>
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Contas a Pagar
                      <span className="count-title">{filteredContas.length} contas</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards de Resumo */}
              <div className="row mb-4">
                <div className="col-xl-2 col-sm-6">
                  <div className="card bg-danger-light border-danger">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-danger">
                          <i className="ti ti-clock-exclamation"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">Vencidos</div>
                          <div className="dash-counts">
                            <p>{resumo.vencidos}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-danger fw-bold">
                          {formatCurrency(resumo.vencidos_valor)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-sm-6">
                  <div className="card bg-warning-light border-warning">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-warning">
                          <i className="ti ti-clock-hour-4"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">Vencem Hoje</div>
                          <div className="dash-counts">
                            <p>{resumo.vencem_hoje}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-warning fw-bold">
                          {formatCurrency(resumo.vencem_hoje_valor)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-sm-6">
                  <div className="card bg-secondary-light border-secondary">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-secondary">
                          <i className="ti ti-calendar-time"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">A Vencer</div>
                          <div className="dash-counts">
                            <p>{resumo.a_vencer}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-secondary fw-bold">
                          {formatCurrency(resumo.a_vencer_valor)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-2 col-sm-6">
                  <div className="card bg-success-light border-success">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-success">
                          <i className="ti ti-check"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">Pagos</div>
                          <div className="dash-counts">
                            <p>{resumo.pagos}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-success fw-bold">
                          {formatCurrency(resumo.pagos_valor)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-4 col-sm-12">
                  <div className="card bg-primary-light border-primary">
                    <div className="card-body">
                      <div className="dash-widget-header">
                        <span className="dash-widget-icon text-primary">
                          <i className="ti ti-calculator"></i>
                        </span>
                        <div className="dash-count">
                          <div className="dash-title">Total do Período</div>
                          <div className="dash-counts">
                            <p>{resumo.total_periodo}</p>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-primary fw-bold">
                          {formatCurrency(resumo.total_periodo_valor)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtro de Período */}
              <div className="card mb-4">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <h5 className="card-title mb-0">Filtro de Período</h5>
                    </div>
                    <div className="col-md-6">
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label">Data Início</label>
                          <div className="icon-form">
                            <span className="form-icon">
                              <i className="ti ti-calendar"></i>
                            </span>
                            <DatePicker
                              className="form-control"
                              selected={dataInicio}
                              onChange={(date) => setDataInicio(date)}
                              dateFormat="dd/MM/yyyy"
                              locale="pt-BR"
                              placeholderText="dd/mm/aaaa"
                              popperClassName="date-picker-higher-z-index"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Data Fim</label>
                          <div className="icon-form">
                            <span className="form-icon">
                              <i className="ti ti-calendar"></i>
                            </span>
                            <DatePicker
                              className="form-control"
                              selected={dataFim}
                              onChange={(date) => setDataFim(date)}
                              dateFormat="dd/MM/yyyy"
                              locale="pt-BR"
                              placeholderText="dd/mm/aaaa"
                              popperClassName="date-picker-higher-z-index"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Contas"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-plus me-2"></i>
                          Adicionar Conta
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* /Search */}
                </div>

                <div className="card-body">
                  {/* Lista de Contas */}
                  {isLoading ? (
                    <div className="text-center p-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-2">Carregando contas...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : !isSearching && searchText && filteredContas.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="ti ti-search-off fs-3 text-muted"></i>
                      </div>
                      <h5 className="text-muted">Nenhuma conta encontrada</h5>
                      <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                      <button
                        className="btn btn-outline-primary mt-2"
                        onClick={handleClearSearch}
                      >
                        <i className="ti ti-x me-1"></i>
                        Limpar pesquisa
                      </button>
                    </div>
                  ) : (
                    <ScrollableTable dataSource={filteredContas} columns={columns} />
                  )}
                  {/* /Lista de Contas */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS para melhorar o z-index do DatePicker */}
      <style>
        {`
          .date-picker-higher-z-index {
            z-index: 9999 !important;
          }
          .react-datepicker-popper {
            z-index: 9999 !important;
          }
          .react-datepicker {
            z-index: 9999 !important;
          }
        `}
      </style>

      {/* Modais e Offcanvas */}
      <>
        {/* Add New Conta */}
        <div
          className="offcanvas offcanvas-end offcanvas-large"
          tabIndex={-1}
          id="offcanvas_add"
        >
          <div className="offcanvas-header border-bottom">
            <h4>Adicionar Nova Conta a Pagar</h4>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={clearAnexoDataCreate}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            <form onSubmit={handleCreateConta}>
              <div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Fornecedor <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={fornecedores}
                        className="select"
                        placeholder="Escolher Fornecedor"
                        classNamePrefix="react-select"
                        value={fornecedores.find(f => f.value === formData.id_fornecedor)}
                        onChange={(option) => setFormData({
                          ...formData,
                          id_fornecedor: option?.value || 0
                        })}
                        required
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Categoria <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={categorias}
                        className="select"
                        placeholder="Escolher Categoria"
                        classNamePrefix="react-select"
                        value={categorias.find(c => c.value === formData.id_categoria)}
                        onChange={(option) => setFormData({
                          ...formData,
                          id_categoria: option?.value || 0
                        })}
                        required
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Descrição <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="descricao"
                        value={formData.descricao}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Data de Emissão <span className="text-danger">*</span>
                      </label>
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar-check" />
                        </span>
                        <DatePicker
                          className="form-control datetimepicker"
                          selected={selectedDate}
                          onChange={(date) => setSelectedDate(date)}
                          dateFormat="dd/MM/yyyy"
                          locale="pt-BR"
                          placeholderText="dd/mm/aaaa"
                          required
                          popperClassName="date-picker-higher-z-index"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Data de Vencimento <span className="text-danger">*</span>
                      </label>
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar-check" />
                        </span>
                        <DatePicker
                          className="form-control datetimepicker"
                          selected={selectedDateVencimento}
                          onChange={(date) => setSelectedDateVencimento(date)}
                          dateFormat="dd/MM/yyyy"
                          locale="pt-BR"
                          placeholderText="dd/mm/aaaa"
                          required
                          popperClassName="date-picker-higher-z-index"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Valor <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="valor_original"
                        value={formData.valor_original}
                        onChange={handleFormChange}
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Número do Documento
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="numero_documento"
                        value={formData.numero_documento || ''}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Observações
                      </label>
                      <textarea
                        className="form-control"
                        name="observacoes"
                        value={formData.observacoes || ''}
                        onChange={handleFormChange}
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Seção de Anexo de Documento no Cadastro */}
                  <div className="col-md-12">
                    <div className="card bg-light border-0 mb-3">
                      <div className="card-header bg-transparent border-0 pb-2">
                        <h6 className="mb-0">
                          <i className="ti ti-paperclip me-2"></i>
                          Anexar Documento (Opcional)
                        </h6>
                      </div>
                      <div className="card-body pt-2">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="col-form-label">
                                Título do Documento
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={anexoDataCreate.titulo}
                                onChange={(e) => setAnexoDataCreate({
                                  ...anexoDataCreate,
                                  titulo: e.target.value
                                })}
                                placeholder="Ex: Nota Fiscal, Comprovante"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="col-form-label">
                                Descrição
                              </label>
                              <textarea
                                className="form-control"
                                rows={2}
                                value={anexoDataCreate.descricao}
                                onChange={(e) => setAnexoDataCreate({
                                  ...anexoDataCreate,
                                  descricao: e.target.value
                                })}
                                placeholder="Descrição opcional do documento"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="col-form-label">
                                Arquivo
                              </label>
                              <div className="drag-attach">
                                <input
                                  type="file"
                                  ref={fileInputCreateRef}
                                  onChange={handleFileUploadCreate}
                                  multiple
                                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                />
                                <div className="img-upload">
                                  <i className="ti ti-file-broken" />
                                  Enviar Arquivo
                                </div>
                              </div>
                              <div className="form-text">
                                Formatos aceitos: PDF, JPG, PNG, DOC, DOCX (máx. 10MB)
                              </div>
                            </div>
                          </div>

                          {/* Arquivos selecionados no cadastro */}
                          {uploadedFilesCreate.length > 0 && (
                            <div className="col-md-12">
                              <label className="col-form-label">Arquivos Selecionados</label>
                              {uploadedFilesCreate.map((fileData, index) => (
                                <div key={index} className="upload-file upload-list">
                                  <div>
                                    <h6>{fileData.name}</h6>
                                    <p>{fileData.size}</p>
                                  </div>
                                  <button
                                    onClick={() => handleRemoveFileCreate(index)}
                                    className="text-danger border-0 bg-transparent"
                                    type="button"
                                  >
                                    <i className="ti ti-trash-x" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-light me-2"
                  onClick={clearAnexoDataCreate}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Criando...
                    </>
                  ) : (
                    'Criar Conta'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        {/* /Add New Conta */}

        {/* Edit Conta */}
        <div
          className="offcanvas offcanvas-end offcanvas-large"
          tabIndex={-1}
          id="offcanvas_edit"
        >
          <div className="offcanvas-header border-bottom">
            <h4>Editar Conta a Pagar</h4>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            {isLoading ? (
              <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitEdit}>
                <div>
                  <div className="row">
                    {editError && (
                      <div className="alert alert-danger mb-3" role="alert">
                        <i className="ti ti-alert-circle me-2"></i>
                        {editError}
                      </div>
                    )}
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Fornecedor <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={fornecedores}
                          className="select"
                          placeholder="Escolher Fornecedor"
                          classNamePrefix="react-select"
                          value={fornecedores.find(f => f.value === updateFormData.id_fornecedor)}
                          onChange={(option) => setUpdateFormData({
                            ...updateFormData,
                            id_fornecedor: option?.value || 0
                          })}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Categoria <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={categorias}
                          className="select"
                          placeholder="Escolher Categoria"
                          classNamePrefix="react-select"
                          value={categorias.find(c => c.value === updateFormData.id_categoria)}
                          onChange={(option) => setUpdateFormData({
                            ...updateFormData,
                            id_categoria: option?.value || 0
                          })}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Descrição <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="descricao"
                          value={updateFormData.descricao || ''}
                          onChange={handleUpdateFormChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Data de Emissão <span className="text-danger">*</span>
                        </label>
                        <div className="icon-form">
                          <span className="form-icon">
                            <i className="ti ti-calendar-check" />
                          </span>
                          <DatePicker
                            className="form-control datetimepicker"
                            selected={selectedDateEdit}
                            onChange={(date) => setSelectedDateEdit(date)}
                            dateFormat="dd/MM/yyyy"
                            locale="pt-BR"
                            placeholderText="dd/mm/aaaa"
                            popperClassName="date-picker-higher-z-index"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Data de Vencimento <span className="text-danger">*</span>
                        </label>
                        <div className="icon-form">
                          <span className="form-icon">
                            <i className="ti ti-calendar-check" />
                          </span>
                          <DatePicker
                            className="form-control datetimepicker"
                            selected={selectedDateVencimentoEdit}
                            onChange={(date) => setSelectedDateVencimentoEdit(date)}
                            dateFormat="dd/MM/yyyy"
                            locale="pt-BR"
                            placeholderText="dd/mm/aaaa"
                            popperClassName="date-picker-higher-z-index"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Valor <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          name="valor_original"
                          value={updateFormData.valor_original || 0}
                          onChange={handleUpdateFormChange}
                          required
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Número do Documento
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="numero_documento"
                          value={updateFormData.numero_documento || ''}
                          onChange={handleUpdateFormChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Observações
                        </label>
                        <textarea
                          className="form-control"
                          name="observacoes"
                          value={updateFormData.observacoes || ''}
                          onChange={handleUpdateFormChange}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-end">
                  <button
                    type="button"
                    data-bs-dismiss="offcanvas"
                    className="btn btn-light me-2"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Salvar Alterações
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
        {/* /Edit Conta */}

        {/* Processar Pagamento */}
        <div className="modal fade" id="processar_pagamento" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Processar Pagamento</h4>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Valor do Pagamento <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={pagamentoData.valor}
                          onChange={(e) => setPagamentoData({
                            ...pagamentoData,
                            valor: parseFloat(e.target.value) || 0
                          })}
                          required
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Forma de Pagamento <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={formasPagamento.length > 0 ? formasPagamento : formaPagamentoOptions}
                          className="select"
                          placeholder="Escolher Forma de Pagamento"
                          classNamePrefix="react-select"
                          value={(formasPagamento.length > 0 ? formasPagamento : formaPagamentoOptions).find(f => f.value === pagamentoData.id_forma_pagamento)}
                          onChange={(option) => setPagamentoData({
                            ...pagamentoData,
                            id_forma_pagamento: option?.value as number || formaPagamentoPadrao || 1
                          })}
                          required
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Data do Pagamento <span className="text-danger">*</span>
                        </label>
                        <div className="icon-form">
                          <span className="form-icon">
                            <i className="ti ti-calendar-check" />
                          </span>
                          <DatePicker
                            className="form-control datetimepicker"
                            selected={selectedDatePagamento}
                            onChange={(date) => setSelectedDatePagamento(date)}
                            dateFormat="dd/MM/yyyy"
                            locale="pt-BR"
                            placeholderText="dd/mm/aaaa"
                            required
                            popperClassName="date-picker-higher-z-index"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="col-form-label">
                          Observações
                        </label>
                        <textarea
                          className="form-control"
                          value={pagamentoData.observacoes || ''}
                          onChange={(e) => setPagamentoData({
                            ...pagamentoData,
                            observacoes: e.target.value
                          })}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmProcessarPagamento}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processando...
                    </>
                  ) : (
                    'Confirmar Pagamento'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Processar Pagamento */}

        {/* Delete Conta */}
        <div className="modal fade" id="delete_conta" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">

                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Remover Conta?</h4>
                  <p className="mb-0">
                    Tem certeza de que deseja remover <br /> a conta selecionada?
                  </p>

                  {deleteError && (
                    <div className="alert alert-danger mt-3 mb-0" role="alert">
                      <i className="ti ti-alert-circle me-2"></i>
                      {deleteError}
                    </div>
                  )}

                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancelar
                    </Link>
                    <button
                      onClick={confirmDeleteConta}
                      className="btn btn-danger"
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Excluindo...
                        </>
                      ) : (
                        'Sim, Excluir'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Conta */}

        {/* Anexar Documento */}
        <div className="modal fade" id="anexar_documento" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Anexar Documento</h4>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={clearAnexoData}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmitAnexo}>
                  <div className="mb-3">
                    <label className="col-form-label">
                      Título do Documento <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={anexoData.titulo}
                      onChange={(e) => setAnexoData({
                        ...anexoData,
                        titulo: e.target.value
                      })}
                      placeholder="Ex: Nota Fiscal, Comprovante de Pagamento"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="col-form-label">
                      Descrição
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={anexoData.descricao}
                      onChange={(e) => setAnexoData({
                        ...anexoData,
                        descricao: e.target.value
                      })}
                      placeholder="Descrição opcional do documento"
                    />
                  </div>

                  {/* Upload de arquivos */}
                  <div className="mb-3">
                    <label className="col-form-label">
                      Arquivo <span className="text-danger">*</span>
                    </label>
                    <div className="drag-attach">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                      <div className="img-upload">
                        <i className="ti ti-file-broken" />
                        Enviar Arquivo
                      </div>
                    </div>
                    <div className="form-text">
                      Formatos aceitos: PDF, JPG, PNG, DOC, DOCX (máx. 10MB)
                    </div>
                  </div>

                  {/* Arquivos selecionados */}
                  {uploadedFiles.length > 0 && (
                    <div className="mb-3">
                      <label className="col-form-label">Arquivos Selecionados</label>
                      {uploadedFiles.map((fileData, index) => (
                        <div key={index} className="upload-file upload-list">
                          <div>
                            <h6>{fileData.name}</h6>
                            <p>{fileData.size}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="text-danger border-0 bg-transparent"
                            type="button"
                          >
                            <i className="ti ti-trash-x" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="col-lg-12 text-end modal-btn mt-4">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                      onClick={clearAnexoData}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || uploadedFiles.length === 0 || !anexoData.titulo}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Anexando...
                        </>
                      ) : (
                        'Anexar Documento'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Anexar Documento */}
      </>
    </div>
  );
};

export default ContasAPagar;